import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import PrintIcon from "@mui/icons-material/Print";
import RefreshIcon from "@mui/icons-material/Refresh";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  GlobalStyles,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ListItemText from "@mui/material/ListItemText";
import moment from "moment";
import { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { useGetBranches } from "src/api/generic";
import { REPORT_BRANCH_MULTISELECT_ALL_SENTINEL } from "src/constants/reportBranchSelect";
import {
  useGetTaxReport,
  useGetTaxReportData,
  isTaxReportDataRow,
  isTaxReportSectionRow,
  type TaxReportDataPayload,
  type TaxReportRowData,
} from "src/api/reports";
import DateRangeInput from "src/shared/components/date-range";
import {
  ReportControlBar,
  ReportPageHeading,
  ReportSectionCard,
  reportLayoutTokens,
} from "src/shared/components/report-layout";
import { authState } from "src/store/auth";
import RightArrow from "src/assets/svg-icons/aggregators/right_arrow.svg";
import { downloadAsXLS, oneBranchHasPermission, specificBranchHasPermission } from "src/utils";

type TaxReportForm = {
  startDate: string;
  endDate: string;
  branchIds: number[];
};

const parseTaxReportFilters = (sp: URLSearchParams): TaxReportForm => {
  const b = sp.getAll("branches");
  return {
    startDate:
      sp.get("start_date") || moment().startOf("month").format("yyyy-MM-DD"),
    endDate:
      sp.get("end_date") || moment().endOf("month").format("yyyy-MM-DD"),
    branchIds: b.length ? b.map((x) => Number(x)).filter((n) => Number.isFinite(n)) : [],
  };
};

const formatTaxAmount = (n: number | null | undefined) => {
  if (n === null || n === undefined) return "—";
  const v = Number(n);
  if (Number.isNaN(v)) return "—";
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);
};

/** Footer row: negatives in parentheses, null as em dash (matches tax summary styling). */
const formatTaxFooterAmount = (n: number | null | undefined) => {
  if (n === null || n === undefined) return "—";
  const v = Number(n);
  if (Number.isNaN(v)) return "—";
  const abs = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(v));
  if (v < 0) {
    return `(${abs})`;
  }
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);
};

const TaxReportPage = () => {
  const { t } = useTranslation("reports");
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useRecoilValue(authState);
  const companyName = auth.user?.company?.name;

  const [appliedFilters, setAppliedFilters] = useState<TaxReportForm>(() =>
    parseTaxReportFilters(new URLSearchParams(searchParams.toString()))
  );
  const [draftFilters, setDraftFilters] = useState<TaxReportForm>(() =>
    parseTaxReportFilters(new URLSearchParams(searchParams.toString()))
  );

  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [modalDraft, setModalDraft] = useState<TaxReportForm>(() =>
    parseTaxReportFilters(new URLSearchParams(searchParams.toString()))
  );

  const { startDate, endDate, branchIds } = appliedFilters;

  const { data: branchesData } = useGetBranches();
  const branchesHavePermission =
    branchesData?.data.filter((br) =>
      specificBranchHasPermission("download_third_eye_report", br.id)
    ) ?? [];

  const {
    data: reportBody,
    isFetching,
    refetch,
  } = useGetTaxReportData({
    start_date: startDate,
    end_date: endDate,
    branches: branchIds.length > 0 ? branchIds : undefined,
    enabled: true,
  });

  const { refetch: refetchDownload, isFetching: isDownloadLoading } = useGetTaxReport({
    start_date: startDate,
    end_date: endDate,
    branches: branchIds.length > 0 ? branchIds : undefined,
    enabled: false,
  });

  const downloadReportsPermission = oneBranchHasPermission("download_third_eye_report");

  const payload = (reportBody as { data?: TaxReportDataPayload } | null | undefined)
    ?.data;

  const branchColumns = payload?.branches ?? [];

  const { tableBodyRows, totalDueFooterRow } = useMemo(() => {
    const rows = payload?.rows ?? [];
    const footer = rows.find(
      (r): r is TaxReportRowData => isTaxReportDataRow(r) && r.id === "total_due"
    );
    const body = rows.filter(
      (r) => !(isTaxReportDataRow(r) && r.id === "total_due")
    );
    return { tableBodyRows: body, totalDueFooterRow: footer };
  }, [payload?.rows]);

  const periodLabel = useMemo(
    () =>
      t("reportDateRange", {
        start: moment(startDate).format("D MMM YYYY"),
        end: moment(endDate).format("D MMM YYYY"),
      }),
    [t, startDate, endDate]
  );

  const openFilterModal = () => {
    setModalDraft({ ...draftFilters });
    setFilterModalOpen(true);
  };

  const cancelFilterModal = () => setFilterModalOpen(false);

  const commitFiltersFromModal = () => {
    setDraftFilters({ ...modalDraft });
    setFilterModalOpen(false);
  };

  const syncAppliedToUrl = useCallback(
    (next: TaxReportForm) => {
      const p = new URLSearchParams();
      p.set("start_date", next.startDate);
      p.set("end_date", next.endDate);
      next.branchIds.forEach((id) => p.append("branches", String(id)));
      setSearchParams(p);
    },
    [setSearchParams]
  );

  const handleReportUpdate = () => {
    setAppliedFilters({ ...draftFilters });
    syncAppliedToUrl(draftFilters);
    void refetch();
  };

  const handlePrint = () => window.print();

  const handleDownload = useCallback(async () => {
    const result = await refetchDownload();
    const d = result.data as string | undefined;
    if (d != null) {
      downloadAsXLS(`\uFEFF${d}`, "tax-report");
    }
  }, [refetchDownload]);

  const colCount = 2 + branchColumns.length;

  const branchSelectControl = (
    value: TaxReportForm["branchIds"],
    onChange: (ids: number[]) => void
  ) => (
    <FormControl size="small" sx={{ minWidth: 240 }}>
      <InputLabel id="tax-report-branches" shrink>
        {generalT("branch")}
      </InputLabel>
      <Select<string[]>
        labelId="tax-report-branches"
        id="tax-report-branches-select"
        multiple
        value={value.map((id) => String(id))}
        onChange={(e: SelectChangeEvent<string[]>) => {
          const v = e.target.value;
          const next = typeof v === "string" ? v.split(",") : v;
          const strs = next as string[];
          if (strs.includes(REPORT_BRANCH_MULTISELECT_ALL_SENTINEL)) {
            onChange([]);
            return;
          }
          const ids = strs.map(Number).filter((n) => n > 0);
          onChange(ids);
        }}
        input={<OutlinedInput label={generalT("branch")} />}
        renderValue={(selected) => {
          if (selected.length === 0) return t("allBranches");
          return selected
            .map(
              (idStr) =>
                branchesHavePermission.find((b) => String(b.id) === idStr)?.name ?? idStr
            )
            .join(", ");
        }}
      >
        <MenuItem
          key="__all-branches-tax"
          value={REPORT_BRANCH_MULTISELECT_ALL_SENTINEL}
          sx={{
            borderBottom: "1px solid #EAECF0",
            bgcolor: "#F9FAFB",
          }}
        >
          <Typography fontWeight={600} variant="body2" component="span">
            {t("allBranches")}
          </Typography>
        </MenuItem>
        {branchesHavePermission.map((b) => (
          <MenuItem key={b.id} value={String(b.id)}>
            <Checkbox size="small" checked={value.includes(b.id)} />
            <ListItemText primary={b.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const dateRangeControl = (
    draft: TaxReportForm,
    setDraft: Dispatch<SetStateAction<TaxReportForm>>
  ) => (
    <Box sx={{ minWidth: 250 }}>
      <DateRangeInput
        startDate={draft.startDate ? moment(draft.startDate) : null}
        startDateId="tax-report-start"
        endDate={draft.endDate ? moment(draft.endDate) : null}
        endDateId="tax-report-end"
        onDatesChange={(arg: {
          startDate: moment.Moment | null;
          endDate: moment.Moment | null;
        }) => {
          const s = arg.startDate?.format("yyyy-MM-DD");
          const e = arg.endDate?.format("yyyy-MM-DD");
          if (s && e) {
            setDraft((prev) => ({ ...prev, startDate: s, endDate: e }));
          }
        }}
      />
    </Box>
  );

  return (
    <Box p={{ xs: 2, sm: "12px 32px 32px" }} maxWidth="100%">
      <GlobalStyles
        styles={{
          "@media print": {
            "body *": { visibility: "hidden" },
            "#tax-report-print-root, #tax-report-print-root *": {
              visibility: "visible",
            },
            "#tax-report-print-root": {
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
            },
          },
        }}
      />

      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        mb={1.5}
        sx={{ "@media print": { display: "none" } }}
      >
        <Typography
          fontSize="0.9rem"
          color="#475467"
          onClick={() => navigate("/reports")}
          sx={{ cursor: "pointer" }}
        >
          {t("thirdEyeReport")}
        </Typography>
        <img src={RightArrow} alt="" style={{ width: 12, height: 12 }} />
        <Box borderRadius="4px" bgcolor="#EAECF0" px={1} py={0.5}>
          <Typography color="#344054" fontWeight={600} fontSize="0.85rem">
            {t("taxReport")}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ "@media print": { display: "none" } }} mb={2}>
        <ReportControlBar
          left={
            <>
              {dateRangeControl(draftFilters, setDraftFilters)}
              {branchSelectControl(draftFilters.branchIds, (ids) =>
                setDraftFilters((p) => ({ ...p, branchIds: ids }))
              )}
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<FilterListIcon fontSize="small" />}
                onClick={openFilterModal}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("filter")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon fontSize="small" />}
                onClick={handleReportUpdate}
                disabled={isFetching}
                sx={{ height: 40, minWidth: 120 }}
              >
                {t("reportUpdate")}
              </Button>
            </>
          }
          right={
            <>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<PrintIcon fontSize="small" />}
                onClick={handlePrint}
                sx={{ height: 40, minWidth: 100, borderColor: "#D0D5DD", color: "#344054" }}
              >
                {t("print")}
              </Button>
              <LoadingButton
                variant="outlined"
                startIcon={<FileDownloadIcon fontSize="small" />}
                onClick={handleDownload}
                loading={isDownloadLoading}
                disabled={!downloadReportsPermission}
                sx={{ height: 40, minWidth: 100, borderColor: "#D0D5DD", color: "#344054" }}
              >
                {t("export")}
              </LoadingButton>
            </>
          }
        />
      </Box>

      <Dialog open={filterModalOpen} onClose={cancelFilterModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
          <FilterListIcon sx={{ color: "#475467", fontSize: 22 }} />
          <Typography component="span" fontWeight={700} flex={1}>
            {t("reportFilters")}
          </Typography>
          <IconButton aria-label="close" onClick={cancelFilterModal} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            {dateRangeControl(modalDraft, setModalDraft)}
            {branchSelectControl(modalDraft.branchIds, (ids) =>
              setModalDraft((p) => ({ ...p, branchIds: ids }))
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, flexDirection: "column", gap: 1 }}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={commitFiltersFromModal}
            disabled={isFetching}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {t("applyFilters")}
          </Button>
          <Button color="inherit" fullWidth onClick={cancelFilterModal} sx={{ textTransform: "none" }}>
            {generalT("cancel")}
          </Button>
        </DialogActions>
      </Dialog>

      <Box id="tax-report-print-root">
        <ReportPageHeading
          title={t("taxReport")}
          subtitle={companyName}
          periodLabel={periodLabel}
        />

        <ReportSectionCard>
          {isFetching && !payload ? (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer
              sx={{
                maxWidth: "100%",
                overflowX: "auto",
              }}
            >
              <Table
                size="small"
                stickyHeader
                sx={{ minWidth: branchColumns.length * 120 + 280 }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        bgcolor: "#F2F4F7",
                        color: "#344054",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        borderBottom: "1px solid #EAECF0",
                        py: 1.5,
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <TableCell align="left">{t("accountColumn")}</TableCell>
                    {branchColumns.map((b) => (
                      <TableCell key={b.id} align="right">
                        {b.name}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {t("totalAllTaxes")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableBodyRows.map((row, idx) => {
                    if (isTaxReportSectionRow(row)) {
                      return (
                        <TableRow
                          key={`sec-${row.id}-${idx}`}
                          sx={{ bgcolor: "#FDF1E6" }}
                        >
                          <TableCell
                            colSpan={colCount}
                            sx={{
                              color: "#7A4A2A",
                              fontWeight: 700,
                              fontSize: "0.8rem",
                              textTransform: "uppercase",
                              borderColor: reportLayoutTokens.cardBorder,
                              py: 1.25,
                            }}
                          >
                            {row.label}
                          </TableCell>
                        </TableRow>
                      );
                    }
                    if (!isTaxReportDataRow(row)) {
                      return null;
                    }
                    const dataRow = row;
                    return (
                      <TableRow
                        key={dataRow.id}
                        sx={{
                          "& td": {
                            borderColor: "#EEF0F2",
                            fontSize: "0.875rem",
                            color: "#101828",
                            py: 1.25,
                          },
                        }}
                      >
                        <TableCell
                          align="left"
                          sx={{
                            fontWeight: dataRow.account_bold ? 600 : 400,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {dataRow.label}
                        </TableCell>
                        {dataRow.cells.map((cell, i) => (
                          <TableCell key={`c-${i}`} align="right">
                            {formatTaxAmount(cell)}
                          </TableCell>
                        ))}
                        <TableCell
                          align="right"
                          sx={{ fontWeight: dataRow.total_bold ? 700 : 500 }}
                        >
                          {formatTaxAmount(dataRow.total)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                {totalDueFooterRow != null && (
                  <TableFooter
                    sx={{
                      "& tr": {
                        backgroundColor: "#F9FAFB",
                        borderTop: "2px solid",
                        borderColor: "primary.main",
                        "& td": {
                          color: "#101828",
                          fontSize: "0.875rem",
                          fontWeight: 700,
                          py: 1.5,
                          borderBottom: "none",
                        },
                        "& td:first-of-type": {
                          borderBottomLeftRadius: 8,
                        },
                        "& td:last-of-type": {
                          borderBottomRightRadius: 8,
                        },
                      },
                    }}
                  >
                    <TableRow>
                      <TableCell
                        align="left"
                        component="th"
                        scope="row"
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {t("totalDueToGovernment")}
                      </TableCell>
                      {totalDueFooterRow.cells.map((cell, i) => (
                        <TableCell key={`ft-${i}`} align="right" component="td">
                          {formatTaxFooterAmount(cell)}
                        </TableCell>
                      ))}
                      <TableCell align="right" component="td">
                        {formatTaxFooterAmount(totalDueFooterRow.total)}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </TableContainer>
          )}
        </ReportSectionCard>
      </Box>
    </Box>
  );
};

export default TaxReportPage;
